/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { Image } from "@packages/framework";
import { getDeviceOS, URL_APP_STORE, URL_PLAY_STORE } from "@packages/sdk";
import type { MouseEventHandler } from "react";

import { numericPixels, spacing } from "../../../../../global/emotion";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import {
  Apple2Icon,
  GooglePlayIcon,
} from "../../../_base/icons/Icon/ExternalIcons";
import type { IconElement } from "../../../_base/icons/Icon/Icon";

const stylesDesktop: Record<string, Interpolation> = {
  appleIcon: {
    marginLeft: -2,
  },
  buttonLink: {
    alignItems: "center",
    display: "flex",
  },
  downloadButton: {
    justifyContent: "flex-start",
    width: "fit-content",
  },
  downloadButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.s,
  },
  withQr: {
    alignItems: "center",
    display: "flex",
    gap: spacing.m,
    justifyContent: "center",
  },
  qrImage: {
    height: numericPixels[72],
    width: numericPixels[72],
  },
  buttonsWithQr: {
    justifyContent: "space-between",
  },
};

const stylesMobile: Record<string, Interpolation> = {
  appleIcon: {
    marginLeft: -2,
  },
  buttonLink: {
    justifyContent: "space-between",
    width: "100%",
  },
  googleIcon: {
    marginLeft: 2,
  },
};
const iconPlaceholderStyle: Interpolation<{ size: number }> = (props) => ({
  width: props.size,
});

export type DownloadCTAProps = {
  handleDownload: (props: { platform: "apple" | "google" }) => void;
};

type CTALinkProps = {
  iconCSS?: Interpolation;
  onClickCapture?: MouseEventHandler<any>;
  size: "s" | "l";
  endIcon?: IconElement;
  urlOverride?: string;
  iconSize?: number;
  className?: string;
};

const AppleLink = ({
  iconCSS = {},
  onClickCapture,
  iconSize = 28,
  endIcon = null,
  size,
  urlOverride,
  className = "",
}: CTALinkProps) => (
  <Button
    asChild
    onClickCapture={onClickCapture}
    size={size}
    startIcon={<Apple2Icon height={iconSize} width={iconSize} css={iconCSS} />}
    endIcon={endIcon}
    className={className}
    variant="outline"
  >
    <a href={urlOverride ?? URL_APP_STORE} target={"_blank"}>
      <I18nMessage message="download_app_store" />
    </a>
  </Button>
);

const GoogleLink = ({
  iconCSS = {},
  className = "",
  onClickCapture,
  iconSize = 16,
  endIcon = null,
  size,
  urlOverride,
}: CTALinkProps) => (
  <Button
    asChild
    onClickCapture={onClickCapture}
    size={size}
    startIcon={
      <GooglePlayIcon height={iconSize} width={iconSize} css={iconCSS} />
    }
    endIcon={endIcon}
    className={className}
    variant="outline"
  >
    <a href={urlOverride ?? URL_PLAY_STORE} target={"_blank"}>
      <I18nMessage message="alt_download_google_button_text" />
    </a>
  </Button>
);

export const DesktopDownloadCTA = ({
  handleDownload,
  withQr = false,
}: DownloadCTAProps & { withQr?: boolean }) => {
  const buttons = (
    <div
      css={[
        stylesDesktop.downloadButtonsContainer,
        withQr && stylesDesktop.buttonsWithQr,
      ]}
    >
      <AppleLink
        iconCSS={[stylesDesktop.appleIcon]}
        css={[stylesDesktop.downloadButton, stylesDesktop.buttonLink]}
        onClickCapture={() => handleDownload({ platform: "apple" })}
        size={"s"}
      />
      <GoogleLink
        css={[stylesDesktop.downloadButton, stylesDesktop.buttonLink]}
        onClickCapture={() => handleDownload({ platform: "google" })}
        size={"s"}
      />
    </div>
  );

  if (withQr) {
    return (
      <div css={stylesDesktop.withQr}>
        <Image
          src={"/assets/qr/qr-home.webp"}
          alt=""
          css={stylesDesktop.qrImage}
          width={300}
          height={300}
        />
        {buttons}
      </div>
    );
  }

  return buttons;
};

export const MobileDownloadCTA = ({
  handleDownload,
  appStoreOverride,
}: DownloadCTAProps & { appStoreOverride?: string }) => {
  if (getDeviceOS() === "android")
    return (
      <GoogleLink
        size={"l"}
        endIcon={<div css={iconPlaceholderStyle({ size: 20 })} />}
        iconSize={20}
        iconCSS={[stylesMobile.googleIcon]}
        css={stylesMobile.buttonLink}
        onClickCapture={() => handleDownload({ platform: "google" })}
        urlOverride={appStoreOverride}
      />
    );

  return (
    <AppleLink
      size={"l"}
      iconSize={32}
      endIcon={<div css={iconPlaceholderStyle({ size: 32 })} />}
      iconCSS={[stylesMobile.appleIcon]}
      css={stylesMobile.buttonLink}
      urlOverride={appStoreOverride}
      onClickCapture={() => handleDownload({ platform: "apple" })}
    />
  );
};
